export type CommunicationPlaybookNodeState = 'observed' | 'completed' | 'pending';

export type CommunicationWatchPlanSummary = {
  schemaVersion?: unknown;
  status?: unknown;
  channels?: unknown;
  constraintKindCounts?: unknown;
  counts?: unknown;
  confidenceThreshold?: unknown;
  humanConfirmationRequired?: unknown;
  evidenceWindow?: unknown;
  sourceHonesty?: unknown;
  updatedAt?: unknown;
};

export type CommunicationWatchEvidenceSummary = {
  schemaVersion?: unknown;
  watchPlanStatus?: unknown;
  status?: unknown;
  statusLabel?: unknown;
  completedSourceLabel?: unknown;
  completedAt?: unknown;
  statusCounts?: unknown;
  channelCounts?: unknown;
  latestObservedAt?: unknown;
  matchedConstraintKindCounts?: unknown;
  counts?: unknown;
  sourceHonesty?: unknown;
  updatedAt?: unknown;
};

export type CommunicationProfileHintSummary = {
  kind: string;
  value: string | null;
  confidence: string;
  status: string;
  observedAt: string;
  sourceKind: string;
  sourceRefCount: number;
};

export type CommunicationProfileCandidateSummary = {
  candidateId: string;
  displayName: string | null;
  primaryPhone: string | null;
  primaryEmail: string | null;
  serviceAddress: string | null;
  projectTerms: string[];
  confidence: 'low' | 'medium' | 'high';
  status: 'candidate' | 'needs_review' | 'confirmed' | 'rejected';
  evidenceSummary: string;
  counts: {
    hintCount: number;
    sourceNodeCount: number;
    sourceReferenceCount: number;
  };
  updatedAt: string;
};

export type CommunicationEngineNode = {
  id: string;
  nodeId?: string;
  title: string;
  body: string | null;
  kind: string;
  lifecycleState: string;
  watchPlanSummary?: CommunicationWatchPlanSummary;
  watchEvidenceSummary?: CommunicationWatchEvidenceSummary;
  profileHints?: CommunicationProfileHintSummary[];
  profileCandidate?: CommunicationProfileCandidateSummary | null;
  playbookNode?: {
    state: CommunicationPlaybookNodeState;
    industryProfileKey: string | null;
    industryCategoryKey: string | null;
    industryStageKey: string | null;
    industryTimelineTemplateKey: string | null;
    watchGuidance?: {
      status?: unknown;
      dueHint?: unknown;
      source?: unknown;
      watchPlanSummary?: CommunicationWatchPlanSummary;
      watchEvidenceSummary?: CommunicationWatchEvidenceSummary;
    };
  };
  lastEvidenceAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type EngineJourneyWatchItem = {
  key: string;
  title: string;
  statusLabel: string;
  detail: string;
  tone: 'emerald' | 'amber' | 'slate';
};

export type EngineJourneySummaryDisplay = {
  status: 'empty' | 'ready';
  title: string;
  detail: string;
  completedCountLabel: string;
  pendingCountLabel: string;
  watchItems: EngineJourneyWatchItem[];
  safetyLabel: string;
};

export type CommunicationProfileCandidateAction = 'confirm' | 'reject' | 'mark_needs_review' | 'mark_candidate';

export type CommunicationProfileCandidateDisplay = {
  status: 'empty' | 'ready';
  actionCandidateId: string | null;
  availableActions: CommunicationProfileCandidateAction[];
  title: string;
  badgeLabel: string;
  tone: 'emerald' | 'amber' | 'slate';
  detail: string;
  fields: Array<{ label: string; value: string }>;
  projectTerms: string[];
  evidenceSummary: string | null;
  linkedNodesLabel: string;
  safetyLabel: string;
};

export function buildEngineJourneySummaryDisplay(nodes: CommunicationEngineNode[]): EngineJourneySummaryDisplay {
  const safeNodes = Array.isArray(nodes) ? nodes.filter(isRecord) : [];
  const playbookNodes = safeNodes.filter(node => isRecord(node.playbookNode)) as CommunicationEngineNode[];
  const completed = playbookNodes.filter(node => node.playbookNode?.state === 'completed' || node.playbookNode?.state === 'observed' || node.lifecycleState === 'resolved' || node.lifecycleState === 'active');
  const pending = playbookNodes.filter(node => node.playbookNode?.state === 'pending' || node.lifecycleState === 'candidate');

  if (playbookNodes.length === 0) {
    return {
      status: 'empty',
      title: 'No customer journey yet',
      detail: 'After a call, email, or calendar item is processed, the main customer steps will appear here.',
      completedCountLabel: '0 done',
      pendingCountLabel: '0 pending',
      watchItems: [],
      safetyLabel: 'Shows saved customer-step summaries only. Raw provider IDs and secrets stay hidden.',
    };
  }

  return {
    status: 'ready',
    title: 'Customer journey',
    detail: 'Shows what has happened and what Oracord is waiting for next.',
    completedCountLabel: `${completed.length} done`,
    pendingCountLabel: `${pending.length} pending`,
    watchItems: pending.slice(0, 4).map(node => watchItemForNode(node)),
    safetyLabel: 'Pending steps stay grey until Oracord sees matching call, email, calendar, or text evidence.',
  };
}


export function buildCommunicationProfileCandidateDisplay(nodes: CommunicationEngineNode[]): CommunicationProfileCandidateDisplay {
  const safeNodes = Array.isArray(nodes) ? nodes.filter(isRecord) : [];
  const candidates = safeNodes
    .map(node => safeProfileCandidateSummary(node.profileCandidate))
    .filter((candidate): candidate is CommunicationProfileCandidateSummary => Boolean(candidate))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const candidate = candidates[0] ?? null;

  if (!candidate) {
    return {
      status: 'empty',
      actionCandidateId: null,
      availableActions: [],
      title: 'Unknown customer',
      badgeLabel: 'No candidate',
      tone: 'slate',
      detail: 'No customer name, phone, email, or address has been safely linked yet.',
      fields: [],
      projectTerms: [],
      evidenceSummary: null,
      linkedNodesLabel: '0 linked nodes',
      safetyLabel: 'Only safe customer clues are shown here. Raw provider records and IDs stay hidden.',
    };
  }

  const fields = [
    { label: 'Phone', value: candidate.primaryPhone },
    { label: 'Email', value: candidate.primaryEmail },
    { label: 'Address', value: candidate.serviceAddress },
  ].filter((item): item is { label: string; value: string } => Boolean(item.value));
  const linkedNodeCount = candidate.counts.sourceNodeCount;
  return {
    status: 'ready',
    actionCandidateId: candidate.candidateId,
    availableActions: profileCandidateActionsForStatus(candidate.status),
    title: candidate.displayName ?? (candidate.status === 'needs_review' ? 'Needs review' : 'Unknown customer'),
    badgeLabel: `${candidateStatusLabel(candidate.status)} · ${candidate.confidence} confidence`,
    tone: candidate.status === 'confirmed' || candidate.confidence === 'high' ? 'emerald' : candidate.status === 'needs_review' || candidate.confidence === 'medium' || candidate.confidence === 'low' ? 'amber' : 'slate',
    detail: candidate.status === 'confirmed'
      ? 'Confirmed customer linked to this conversation.'
      : candidate.status === 'needs_review'
        ? 'Oracord found customer clues, but a person should review them before treating this as the final customer.'
        : 'Oracord found customer clues from the conversation. Confirm them before treating this as final.',
    fields,
    projectTerms: candidate.projectTerms.slice(0, 6),
    evidenceSummary: candidate.evidenceSummary,
    linkedNodesLabel: `${linkedNodeCount} linked node${linkedNodeCount === 1 ? '' : 's'} · ${candidateStatusLabel(candidate.status)}`,
    safetyLabel: 'Confirm/reject only changes this customer candidate. It does not expose raw provider records.',
  };
}

function profileCandidateActionsForStatus(status: CommunicationProfileCandidateSummary['status']): CommunicationProfileCandidateAction[] {
  if (status === 'candidate') return ['confirm', 'reject', 'mark_needs_review'];
  if (status === 'needs_review') return ['confirm', 'reject', 'mark_candidate'];
  if (status === 'confirmed') return ['reject', 'mark_needs_review'];
  return ['mark_needs_review', 'mark_candidate'];
}

export function watchDetailForNode(node: CommunicationEngineNode): string {
  const guidance = isRecord(node.playbookNode?.watchGuidance) ? node.playbookNode.watchGuidance : null;
  const summary = getWatchPlanSummary(node);
  const dueHint = guidance ? safeString(guidance.dueHint) : null;

  if (summary) {
    return formatWatchPlanSummaryDetail(summary, dueHint, getWatchEvidenceSummary(node));
  }

  return dueHint ?? (guidance ? null : extractDueHint(node.body)) ?? plainPendingDetail(node.title);
}

function watchItemForNode(node: CommunicationEngineNode): EngineJourneyWatchItem {
  const summary = getWatchPlanSummary(node);
  const detail = watchDetailForNode(node);
  const hasGuidance = summary || isRecord(node.playbookNode?.watchGuidance) && safeString(node.playbookNode.watchGuidance.dueHint);
  return {
    key: safeString(node.nodeId) ?? safeString(node.id) ?? 'pending-watch-node',
    title: safeString(node.title) ?? 'Next step',
    statusLabel: summary ? 'Waiting for evidence' : hasGuidance ? 'Waiting' : 'Pending',
    detail,
    tone: summary ? 'emerald' : hasGuidance ? 'amber' : 'slate',
  };
}

function getWatchPlanSummary(node: CommunicationEngineNode): CommunicationWatchPlanSummary | null {
  const guidance = isRecord(node.playbookNode?.watchGuidance) ? node.playbookNode.watchGuidance : null;
  if (isValidWatchPlanSummary(guidance?.watchPlanSummary)) return guidance.watchPlanSummary;
  if (isValidWatchPlanSummary(node.watchPlanSummary)) return node.watchPlanSummary;
  return null;
}

function getWatchEvidenceSummary(node: CommunicationEngineNode): CommunicationWatchEvidenceSummary | null {
  const guidance = isRecord(node.playbookNode?.watchGuidance) ? node.playbookNode.watchGuidance : null;
  if (isValidWatchEvidenceSummary(guidance?.watchEvidenceSummary)) return guidance.watchEvidenceSummary;
  if (isValidWatchEvidenceSummary(node.watchEvidenceSummary)) return node.watchEvidenceSummary;
  return null;
}

const VALID_WATCH_PLAN_STATUSES = new Set(['pending', 'active', 'paused', 'complete', 'not_required']);
const WATCH_PLAN_CHANNEL_LABELS = {
  oracord_call: 'Oracord calls',
  gmail: 'Gmail',
  google_calendar: 'Google Calendar',
  sms: 'SMS',
} as const;
const VALID_WATCH_PLAN_CHANNELS = new Set(Object.keys(WATCH_PLAN_CHANNEL_LABELS));
const VALID_CONFIDENCE = new Set(['low', 'medium', 'high']);
const VALID_PROFILE_CANDIDATE_STATUS = new Set(['candidate', 'needs_review', 'confirmed', 'rejected']);
const VALID_WATCH_EVIDENCE_STATUS = new Set(['pending', 'needs_review', 'completed']);
const UNSAFE_MANAGER_TEXT_PATTERN = /(?:bearer\s+|access[_-]?token|refresh[_-]?token|id[_-]?token|api[_-]?key|client[_-]?secret|password|credential|signed[_-]?url|https?:\/\/|gmail\.google|calendar\.google|provider|private staff|raw transcript|raw email|raw calendar|thread[_ -]?id|message[_ -]?id|call[_ -]?sid|source[_ -]?id|external[_ -]?id)/i;

function isValidWatchPlanSummary(value: unknown): value is CommunicationWatchPlanSummary {
  if (!isRecord(value)) return false;
  const sourceHonesty = isRecord(value.sourceHonesty) ? value.sourceHonesty : null;
  if (
    value.schemaVersion !== '2026-05-12' ||
    !VALID_WATCH_PLAN_STATUSES.has(String(value.status)) ||
    !VALID_CONFIDENCE.has(String(value.confidenceThreshold)) ||
    !sourceHonesty ||
    sourceHonesty.contractOnly !== true ||
    sourceHonesty.scannerRuntimeImplemented !== false ||
    sourceHonesty.liveProviderFetchPerformed !== false ||
    sourceHonesty.rawSourceStored !== false
  ) {
    return false;
  }
  if (!Array.isArray(value.channels)) return false;
  return value.channels.every(item => isRecord(item) && VALID_WATCH_PLAN_CHANNELS.has(String(item.channel)));
}

function isValidWatchEvidenceSummary(value: unknown): value is CommunicationWatchEvidenceSummary {
  if (!isRecord(value)) return false;
  const sourceHonesty = isRecord(value.sourceHonesty) ? value.sourceHonesty : null;
  if (
    value.schemaVersion !== '2026-05-12' ||
    !VALID_WATCH_PLAN_STATUSES.has(String(value.watchPlanStatus)) ||
    !VALID_WATCH_EVIDENCE_STATUS.has(String(value.status)) ||
    !sourceHonesty ||
    sourceHonesty.contractOnly !== true ||
    typeof sourceHonesty.scannerRuntimeImplemented !== 'boolean' ||
    sourceHonesty.liveProviderFetchPerformed !== false ||
    sourceHonesty.rawSourceStored !== false ||
    sourceHonesty.rawSourceDetailsExposed !== false
  ) {
    return false;
  }
  if (!isRecord(value.statusCounts) || !isRecord(value.channelCounts) || !isRecord(value.counts) || !isRecord(value.matchedConstraintKindCounts)) return false;
  if (value.completedSourceLabel !== null && value.completedSourceLabel !== undefined && !safeEvidenceSourceLabel(value.completedSourceLabel)) return false;
  if (value.completedAt !== null && value.completedAt !== undefined && !safeIsoishString(value.completedAt)) return false;
  return true;
}

function formatWatchPlanSummaryDetail(summary: CommunicationWatchPlanSummary, dueHint: string | null, evidence: CommunicationWatchEvidenceSummary | null): string {
  const channelLabels = safeChannelLabels(summary.channels);
  const counts = isRecord(summary.counts) ? summary.counts : {};
  const boundedConstraintCount = safeNumber(counts.constraintCount);
  const hardBoundaryCount = safeNumber(counts.hardBoundaryConstraintCount);
  const queryHintCount = safeNumber(counts.queryHintCount);
  const completionRuleCount = safeNumber(counts.completionRuleCount);
  const confidenceThreshold = safeString(summary.confidenceThreshold);
  const humanConfirmationRequired = typeof summary.humanConfirmationRequired === 'boolean' ? summary.humanConfirmationRequired : null;
  const sourceHonesty = isRecord(summary.sourceHonesty) ? summary.sourceHonesty : {};
  const scannerRuntimeImplemented = sourceHonesty.scannerRuntimeImplemented === true;
  const liveProviderFetchPerformed = sourceHonesty.liveProviderFetchPerformed === true;

  const parts = [
    `Oracord is waiting${channelLabels.length ? ` for ${formatList(channelLabels)}` : ''}.`,
  ];
  const countParts = [
    boundedConstraintCount === null ? null : `${boundedConstraintCount} bounded constraint${boundedConstraintCount === 1 ? '' : 's'}`,
    hardBoundaryCount === null ? null : `${hardBoundaryCount} hard boundar${hardBoundaryCount === 1 ? 'y' : 'ies'}`,
    queryHintCount === null ? null : `${queryHintCount} safe query hint${queryHintCount === 1 ? '' : 's'}`,
    completionRuleCount === null ? null : `${completionRuleCount} completion rule${completionRuleCount === 1 ? '' : 's'}`,
  ].filter((item): item is string => Boolean(item));
  if (countParts.length) parts.push(`${formatList(countParts)} saved.`);
  if (confidenceThreshold) parts.push(`Needs ${confidenceThreshold} confidence or better.`);
  if (humanConfirmationRequired !== null) parts.push(humanConfirmationRequired ? 'A person must approve it before this turns green.' : 'Can turn green automatically when evidence matches.');
  const evidenceDetail = evidence ? formatWatchEvidenceSummaryDetail(evidence, channelLabels) : null;
  if (evidenceDetail) parts.push(evidenceDetail);
  if (dueHint) parts.push(dueHint);
  parts.push(scannerRuntimeImplemented || liveProviderFetchPerformed
    ? 'Provider activity is hidden here.'
    : 'This card is a plain-English plan, not raw provider data.');
  return parts.join(' ');
}

function formatWatchEvidenceSummaryDetail(summary: CommunicationWatchEvidenceSummary, plannedChannelLabels: string[]): string | null {
  const statusCounts = isRecord(summary.statusCounts) ? summary.statusCounts : {};
  const counts = isRecord(summary.counts) ? summary.counts : {};
  const total = safeNumber(counts.totalCandidateCount);
  const candidate = safeNumber(statusCounts.candidate) ?? 0;
  const needsReview = safeNumber(statusCounts.needsHumanReview) ?? 0;
  const accepted = safeNumber(statusCounts.accepted) ?? 0;
  const rejected = safeNumber(statusCounts.rejected) ?? 0;
  const latestObservedAt = safeString(summary.latestObservedAt);
  const evidenceStatus = safeString(summary.status);
  const completedSourceLabel = safeEvidenceSourceLabel(summary.completedSourceLabel);
  const completedAt = safeIsoishString(summary.completedAt);
  const channels = Object.keys(isRecord(summary.channelCounts) ? summary.channelCounts : {})
    .filter(channel => VALID_WATCH_PLAN_CHANNELS.has(channel))
    .map(channel => WATCH_PLAN_CHANNEL_LABELS[channel as keyof typeof WATCH_PLAN_CHANNEL_LABELS]);
  const waitingChannels = channels.length ? channels : plannedChannelLabels;
  if (total === null || !evidenceStatus || !VALID_WATCH_EVIDENCE_STATUS.has(evidenceStatus)) return null;
  const pieces = evidenceStatus === 'completed' && completedAt
    ? [`Done${completedSourceLabel ? ` from ${completedSourceLabel}` : ''} at ${completedAt}.`]
    : evidenceStatus === 'needs_review'
      ? [`Possible match found; needs review (${candidate} pending, ${needsReview} need review).`]
      : [`Waiting for${waitingChannels.length ? ` ${formatList(waitingChannels)}` : ''} evidence.`];
  pieces.push(`${total} possible match${total === 1 ? '' : 'es'} saved (${candidate} pending, ${needsReview} need review, ${accepted} accepted, ${rejected} rejected).`);
  if (channels.length) pieces.push(`Watching: ${formatList(channels)}.`);
  if (latestObservedAt) pieces.push(`Last seen: ${latestObservedAt}.`);
  return pieces.join(' ');
}

function plainPendingDetail(title: string | null | undefined): string {
  const normalized = (title ?? '').toLowerCase();
  if (normalized.includes('call')) return 'Waiting for the business line to call the customer back.';
  if (normalized.includes('meeting')) return 'Waiting for a calendar event, email, call, or text that confirms a site meeting.';
  if (normalized.includes('quote') || normalized.includes('estimate')) return 'Waiting for an email, call, or text showing the quote or estimate was sent.';
  return 'Waiting for the next real customer update.';
}

export function extractDueHint(body: string | null | undefined): string | null {
  if (typeof body !== 'string') return null;
  const line = body.split('\n').find(item => item.trim().toLowerCase().startsWith('due hint:'));
  const hint = line?.replace(/^\s*due hint:\s*/i, '').trim() ?? '';
  return hint || null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function safeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}


function safeProfileCandidateSummary(value: unknown): CommunicationProfileCandidateSummary | null {
  if (!isRecord(value)) return null;
  const candidateId = safeInternalId(value.candidateId);
  const confidence = safeString(value.confidence);
  const status = safeString(value.status);
  const evidenceSummary = safeDisplayText(value.evidenceSummary, 360);
  const counts = isRecord(value.counts) ? value.counts : null;
  const hintCount = safeNumber(counts?.hintCount);
  const sourceNodeCount = safeNumber(counts?.sourceNodeCount);
  const sourceReferenceCount = safeNumber(counts?.sourceReferenceCount);
  const updatedAt = safeIsoishString(value.updatedAt);
  if (
    !candidateId ||
    !confidence || !VALID_CONFIDENCE.has(confidence) ||
    !status || !VALID_PROFILE_CANDIDATE_STATUS.has(status) ||
    !evidenceSummary ||
    !counts || hintCount === null || sourceNodeCount === null || sourceReferenceCount === null ||
    !updatedAt
  ) return null;

  const displayName = nullableDisplayText(value.displayName, 120);
  const primaryPhone = nullableDisplayText(value.primaryPhone, 80);
  const primaryEmail = nullableDisplayText(value.primaryEmail, 120);
  const serviceAddress = nullableDisplayText(value.serviceAddress, 180);
  const projectTerms = Array.isArray(value.projectTerms)
    ? value.projectTerms.map(item => safeDisplayText(item, 80)).filter((item): item is string => Boolean(item)).slice(0, 20)
    : null;
  if (!projectTerms) return null;

  return {
    candidateId,
    displayName,
    primaryPhone,
    primaryEmail,
    serviceAddress,
    projectTerms,
    confidence: confidence as CommunicationProfileCandidateSummary['confidence'],
    status: status as CommunicationProfileCandidateSummary['status'],
    evidenceSummary,
    counts: { hintCount, sourceNodeCount, sourceReferenceCount },
    updatedAt,
  };
}

function nullableDisplayText(value: unknown, maxLength: number): string | null {
  if (value === null || value === undefined) return null;
  return safeDisplayText(value, maxLength);
}

function safeDisplayText(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength || UNSAFE_MANAGER_TEXT_PATTERN.test(trimmed)) return null;
  return trimmed;
}

function safeInternalId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^[a-zA-Z0-9:_-]{1,220}$/.test(trimmed) && !UNSAFE_MANAGER_TEXT_PATTERN.test(trimmed) ? trimmed : null;
}

function safeIsoishString(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();
  return Number.isFinite(new Date(trimmed).getTime()) ? trimmed : null;
}

function safeEvidenceSourceLabel(value: unknown): string | null {
  const label = safeDisplayText(value, 80);
  if (!label) return null;
  return new Set(Object.values(WATCH_PLAN_CHANNEL_LABELS)).has(label as typeof WATCH_PLAN_CHANNEL_LABELS[keyof typeof WATCH_PLAN_CHANNEL_LABELS]) ? label : null;
}

function candidateStatusLabel(status: CommunicationProfileCandidateSummary['status']): string {
  if (status === 'needs_review') return 'Needs review';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function safeChannelLabels(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const labels: string[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const channel = String(item.channel);
    if (channel in WATCH_PLAN_CHANNEL_LABELS) labels.push(WATCH_PLAN_CHANNEL_LABELS[channel as keyof typeof WATCH_PLAN_CHANNEL_LABELS]);
  }
  return labels;
}

function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
